import {useState, useEffect, useMemo} from "react";
import {getDefaultSubscriptionPlanId} from "@/utils/selling-plan-tiers";

// Check if URL contains ?box=otp parameter for one-time purchase preselection
const checkIsForcedOtp = () => {
  if (typeof window === 'undefined') return false;
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get('box') === 'otp';
};

const isForcedOtp = checkIsForcedOtp();

const useSubscription = (product, defaultSellingPlanId) => {
  const subscriptionGroups =
    product?.sellingPlanGroups?.filter(group =>
      ((group?.name ?? group?.title ?? "") + "")
        .toLowerCase()
        .includes("subscribe"),
    ) ?? [];

  const sellingPlans =
    subscriptionGroups.reduce(
      (acc, next) =>
        next?.selling_plans ? acc.concat(next?.selling_plans) : acc,
      [],
    ) ?? [];

  const oneMonthPlanId = useMemo(
    () => getDefaultSubscriptionPlanId(product),
    [product],
  );

  const hasSubscription = subscriptionGroups.length > 0;
  
  // If URL has ?box=otp, force one-time purchase (isActive = false)
  // Otherwise, default to subscription if available (isActive = true)
  const [isActive, setSubscriptionActive] = useState(
    hasSubscription && !isForcedOtp,
  );
  const [sellingPlanId, setSellingPlanId] = useState(() => {
    if (isForcedOtp) return null;
    return defaultSellingPlanId ?? oneMonthPlanId ?? null;
  });

  const getSellingPlan = id => {
    let sellingPlan = null;

    subscriptionGroups.find(({selling_plans}) => {
      const found = selling_plans.find(plan => plan.id === id);

      if (found) {
        sellingPlan = found;
      }
    });

    return sellingPlan;
  };

  useEffect(() => {
    if (!hasSubscription) {
      setSellingPlanId(null);
    }
  }, [hasSubscription]);

  useEffect(() => {
    if (!isActive) {
      setSellingPlanId(null);
    }
    if (isActive && !sellingPlanId) {
      setSellingPlanId(oneMonthPlanId);
    }
  }, [isActive, oneMonthPlanId, sellingPlanId]);

  return {
    sellingPlans,
    hasSubscription,
    isActive,
    sellingPlanId,
    setSubscriptionActive,
    setSellingPlanId,
    getSellingPlan,
    isForcedOtp, // Export this so components can check if animation should be disabled
  };
};

export default useSubscription;
